import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShoppingBag, UtensilsCrossed, Plane, Landmark, Heart, Mountain,
  Building, Wallet, Briefcase, Cpu, Factory, Truck
} from 'lucide-react';
import axios from 'axios';
import { Section, SectionTitle } from '@/components/shared/Section';
import { fadeUp } from '@/lib/motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map de iconName (string desde backend) → componente Lucide
const ICON_MAP = {
  ShoppingBag, UtensilsCrossed, Plane, Landmark, Heart, Mountain,
  Building, Wallet, Briefcase, Cpu, Factory, Truck,
};

const IndustryCardSkeleton = () => (
  <div className="rounded-2xl overflow-hidden shadow-sm h-56 bg-slate-200 animate-pulse" />
);

const IndustriesBlock = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/industries`)
      .then((r) => setIndustries(Array.isArray(r.data) ? r.data : []))
      .catch(() => setIndustries([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Section data-testid="industries-block">
      <SectionTitle
        badge="INDUSTRIAS"
        title="Experiencia en tu sector"
        subtitle="Conocemos las necesidades específicas de cada industria."
        center
      />
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end" data-testid="industries-loading">
          {Array.from({ length: 8 }).map((_, i) => <IndustryCardSkeleton key={i} />)}
        </div>
      ) : industries.length === 0 ? (
        <div className="text-center text-slate-400 text-sm py-12" data-testid="industries-empty">
          Pronto agregaremos las industrias.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end" data-testid="industries-grid">
          {industries.map((ind, i) => {
            const Icon = ICON_MAP[ind.icon] || Building;
            return (
              <motion.div
                key={ind.slug || i}
                variants={fadeUp}
                className="group relative rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow h-56"
                data-testid={`industry-card-${ind.slug || i}`}
              >
                <img
                  src={ind.image}
                  alt={ind.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050E1F]/90 via-[#050E1F]/50 to-[#050E1F]/20" />
                <div className="relative h-full flex flex-col justify-end p-5 text-center">
                  <Icon className="w-10 h-10 mx-auto mb-2" style={{ color: ind.color || '#0EA5E9' }} strokeWidth={1.5} />
                  <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'Syne,sans-serif' }}>{ind.name}</h3>
                  <p className="absolute left-5 right-5 bottom-5 text-xs text-slate-300 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#050E1F]/70 backdrop-blur-sm rounded-lg px-3 py-2">{ind.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </Section>
  );
};

export default IndustriesBlock;
